/**
 * The customer tab's Employee section on WO create (employee selection).
 *
 * Search the customer's employees (customer = the client's internal org id, or 'walkin') by name, passport or
 * Emirates ID, or create one with Quick add. Once picked, the employee shows as a card with "Change employee".
 * A tab that already has an applicant's details (e.g. one moved out of another tab by a document's name) starts with
 * the search typed in - passport, else Emirates ID, else name - and Quick add pre-filled with them.
 */

import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, Box, Button, Paper, TextField, Typography } from '@mui/material';
import { PersonAdd, Person } from '@mui/icons-material';
import ApiService from '../../services/api.service';
import QuickEmployeeDialog from './QuickEmployeeDialog';

const idLine = (e) => [e.passportNumber && `PP: ${e.passportNumber}`, e.emiratesId && `EID: ${e.emiratesId}`]
  .filter(Boolean).join(' · ') || 'No ID';

// compact: the WO create page's sizes (36px fields and buttons, 0.8125rem text), for the picker and Quick add
const WorkOrderEmployeePicker = ({ customer, employee, onSelect, onClear, onError, applicant = {}, compact = false }) => {
  const initialQuery = (applicant.passportNumber || applicant.emiratesId || applicant.name || '').trim();
  const [input, setInput] = useState(initialQuery);
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [quickOpen, setQuickOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    const q = input.trim();
    if (q.length < 2 || !customer) { setOptions([]); return undefined; }
    timer.current = setTimeout(() => {
      setLoading(true);
      ApiService.searchCustomerEmployees(customer, q)
        .then((res) => setOptions((res?.data?.data || []).map((r) => ({
          uid: r.uid, name: r.name, passportNumber: r.passport_number || '', emiratesId: r.emirates_id || '',
        }))))
        .catch((err) => { setOptions([]); onError?.(err?.response?.data?.message || 'Employee search failed'); })
        .finally(() => setLoading(false));
    }, 400);
    return () => clearTimeout(timer.current);
  }, [input, customer, onError]);

  if (employee) {
    return (
      <Paper variant="outlined" sx={{ p: 1.25, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Person sx={{ color: 'primary.main' }} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: '0.875rem', fontWeight: 600 }} noWrap>{employee.name}</Typography>
        </Box>
        <Button size="small" onClick={onClear} sx={{ textTransform: 'none' }}>Change employee</Button>
      </Paper>
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
        <Autocomplete
          size="small"
          sx={{ flex: 1 }}
          options={options}
          loading={loading}
          filterOptions={(x) => x}
          inputValue={input}
          onInputChange={(e, v, reason) => { if (reason !== 'reset') setInput(v); }}
          getOptionLabel={(o) => o.name || ''}
          isOptionEqualToValue={(a, b) => a.uid === b.uid}
          noOptionsText={input.trim().length < 2 ? 'Type at least 2 characters' : 'No employee found — use Quick add'}
          onChange={(e, v) => { if (v) { onSelect(v); setInput(''); } }}
          openOnFocus
          renderOption={(props, o) => (
            <li {...props} key={o.uid}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', gap: 1 }}>
                <Typography sx={{ fontSize: '0.8125rem' }}>{o.name}</Typography>
                <Typography sx={{ fontSize: '0.75rem', color: 'text.disabled' }}>{idLine(o)}</Typography>
              </Box>
            </li>
          )}
          renderInput={(params) => (
            <TextField {...params} label="Employee" placeholder="Search by name, passport or Emirates ID…" autoFocus={!!initialQuery}
              sx={{ '& .MuiInputBase-input': { fontSize: '0.8125rem' } }} />
          )}
        />
        <Button variant="outlined" size={compact ? 'small' : 'medium'} startIcon={<PersonAdd />} onClick={() => setQuickOpen(true)}
          sx={{ textTransform: 'none', height: compact ? 36 : 40, flexShrink: 0, ...(compact ? { fontSize: '0.8125rem' } : {}) }}>
          Quick add
        </Button>
      </Box>
      <Typography sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 0.5 }}>
        Select the employee first — services and documents open once one is selected.
      </Typography>
      <QuickEmployeeDialog
        open={quickOpen}
        onClose={() => setQuickOpen(false)}
        customer={customer}
        compact={compact}
        initialName={applicant.name?.trim() || (input.trim() !== initialQuery ? input.trim() : '')}
        initialPassportNumber={applicant.passportNumber?.trim() || ''}
        initialEmiratesId={applicant.emiratesId?.trim() || ''}
        onCreated={(emp, failedUploads) => {
          setQuickOpen(false);
          setInput('');
          onSelect(emp);
          if (failedUploads?.length) onError?.(`Employee created, but the ${failedUploads.join(' and ')} could not be uploaded.`);
        }}
      />
    </>
  );
};

export default WorkOrderEmployeePicker;
